import orderBy from 'lodash/orderBy';
import { storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { computed, ref } from 'vue';
import { MarketType, PriceStyle, type SportType, TicketVoucherOptionStatus, TimezoneOffset } from '@/interface/enum';
import type { IVoucher, IVoucherDisplay, IVoucherInput, VoucherUnavailableReason } from '@/interface/voucher';
import teraApi from '@/libraries/apis/teraApi';
import { i18n } from '@/libraries/i18n';
import { parseDate } from '@/libraries/utils/date';
import { useAccountStore } from '@/stores/accountStore';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';

function parseVoucherDate(expiryDate: string) {
    return parseDate(expiryDate, "yyyy-MM-dd'T'HH:mm:ss", TimezoneOffset.Server);
}

export function isVoucherValidForTicket(input: IVoucherInput): boolean {
    if (input.marketType === MarketType.OutRight) return false;
    if (input.marketType === MarketType.MixParlay) return false;
    if (input.priceStyle === PriceStyle.Euro) return input.price >= 1.5;
    if (input.priceStyle === PriceStyle.Indo) return input.price >= 1;
    if (input.priceStyle === PriceStyle.Malay) return input.price >= 0.5 || input.price < 0;
    if (input.priceStyle === PriceStyle.HK) return input.price >= 0.5;
    return false;
}

export function buildVoucherDisplay(raw: IVoucher, input: IVoucherInput): IVoucherDisplay {
    const { formatMoneyWithSymbol } = useAccountStore();
    const now = new Date();
    const hourMillisecond = 60 * 60 * 1000;
    const dateTo = parseVoucherDate(raw.expiryDate);
    const hourDurations = Math.trunc(Math.abs(dateTo.getTime() - now.getTime()) / hourMillisecond);
    const dayDurations = Math.trunc(hourDurations / 24);

    let expireText: string;
    if (dayDurations > 0) {
        expireText = i18n.t('voucherList_day_left', { number: dayDurations });
    } else if (hourDurations > 0) {
        expireText = i18n.t('voucherList_hour_left', { number: hourDurations });
    } else {
        expireText = i18n.t('voucherList_expiring_soon');
    }

    const isValidForTicket = isVoucherValidForTicket(input);
    const isStakeInRange = raw.stake >= input.minBet && raw.stake <= input.maxBet;
    const isAvailable = isValidForTicket && isStakeInRange;

    let unavailableReason: VoucherUnavailableReason | null = null;
    if (!isAvailable) {
        if (!isValidForTicket) {
            unavailableReason = TicketVoucherOptionStatus.UnavailableOddsInvalid;
        } else if (!isStakeInRange) {
            unavailableReason = TicketVoucherOptionStatus.UnavailableStakeRange;
        }
    }

    return {
        ...raw,
        text: `${formatMoneyWithSymbol(raw.stake)} | ${expireText}`,
        isAvailable,
        unavailableReason,
    };
}

export function buildVoucherDisplayList(rawVouchers: IVoucher[], input: IVoucherInput): IVoucherDisplay[] {
    const list = rawVouchers.map(raw => buildVoucherDisplay(raw, input));
    return orderBy(list, [x => x.isAvailable, x => x.expiryDate, x => x.stake], ['desc', 'asc', 'desc']);
}

export async function fetchVouchersSafely(sportType: SportType, leagueId: number): Promise<IVoucher[] | null> {
    const { isVoucherEnabled } = storeToRefs(useFeatureToggleStore());
    if (!isVoucherEnabled.value) {
        return [];
    }
    try {
        return await teraApi.getAvailableSportsVouchers(sportType, leagueId);
    } catch (error) {
        console.error('Failed to fetch vouchers while opening a ticket', error);
        return null;
    }
}

export function useVoucher(input: Ref<IVoucherInput>) {
    const rawVoucher = ref<IVoucher[] | null>([]);

    async function fetchVouchers() {
        rawVoucher.value = await fetchVouchersSafely(input.value.sportType, input.value.leagueId);
    }

    const vouchers = computed<IVoucherDisplay[]>(() => buildVoucherDisplayList(rawVoucher.value ?? [], input.value));

    return {
        fetchVouchers,
        rawVoucher,
        vouchers,
    };
}
